/**
 * IIFE entry for plain <script> embedding. Auto-initialises from data-* attrs:
 *
 *   <script src="https://cdn.example.com/logclip.global.js"
 *           data-logclip-key="pk_live_xxx"
 *           data-logclip-endpoint="https://app.logclip.com"
 *           data-logclip-mask-inputs="true"
 *           data-logclip-require-consent="false"
 *           data-logclip-trace-propagation="true"
 *           data-logclip-trace-hosts="api.example.com, auth.example.com"
 *           data-logclip-auto-start="true"
 *           data-logclip-traffic-sources="false"
 *           data-logclip-idle-timeout="1800000"
 *           data-logclip-max-session="86400000"></script>
 *
 * `data-logclip-idle-timeout` is how long with no user input ends the session
 * (default 30 min); the next click starts a new one. "0" turns it off and gives
 * one unbounded session per page load.
 *
 * `data-logclip-trace-propagation` stamps a W3C traceparent on same-origin (and
 * `data-logclip-trace-hosts`-allowlisted) fetch/XHR so the backend continues the
 * trace — linking this session to its server-side spans in App Monitor.
 *
 * Exposes `window.LogClip` for manual identify()/track()/setConsent() calls.
 * For manual recording control, set data-logclip-auto-start="false" and call
 * LogClip.startRecording() / LogClip.stopRecording() yourself.
 */
import { LogClip } from './index.js';
import type { ObsrviqInitConfig } from '@logclip/types';

declare global {
  interface Window {
    LogClip: typeof LogClip;
  }
}

const current = document.currentScript as HTMLScriptElement | null;

function bool(v: string | null | undefined, fallback: boolean): boolean {
  if (v == null) return fallback;
  return v === 'true' || v === '1';
}

/** A millisecond duration attribute. Returns undefined for absent-or-malformed so
 *  the SDK default applies — `Number('')` is 0 and `Number('30m')` is NaN, and
 *  both would otherwise read as "disable this", the exact opposite of a typo's
 *  intent. "0" itself is a real value and passes through. */
function ms(v: string | null): number | undefined {
  if (v == null || v.trim() === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
}

/** Read `data-logclip-<name>`, falling back through both pre-rebrand attribute
 *  generations so embeds pasted under the old brands keep initialising. These
 *  aliases are permanent — the markup lives in the customer's HTML and is never
 *  coming back to be updated. Mirrors the ingest accepting the legacy key headers. */
function attr(el: HTMLScriptElement, name: string): string | null {
  return (
    el.getAttribute(`data-logclip-${name}`) ??
    el.getAttribute(`data-obsrviq-${name}`) ??
    el.getAttribute(`data-lumera-${name}`)
  );
}

if (current) {
  const key = attr(current, 'key');
  if (key) {
    const cfg: ObsrviqInitConfig = {
      siteKey: key,
      endpoint: attr(current, 'endpoint') || undefined,
      maskAllInputs: bool(attr(current, 'mask-inputs'), true),
      captureNetworkBodies: bool(attr(current, 'capture-bodies'), false),
      // Undefined (attribute absent) falls through to the SDK's 'auto' default
      // rather than being pinned to false here.
      requireConsent: attr(current, 'require-consent') === null
        ? undefined
        : bool(attr(current, 'require-consent'), false),
      askPermission: bool(attr(current, 'ask-permission'), false),
      showRecording: bool(attr(current, 'show-recording'), false),
      recordCanvas: bool(attr(current, 'record-canvas'), false),
      tracePropagation: bool(attr(current, 'trace-propagation'), false),
      autoStart: bool(attr(current, 'auto-start'), true),
    };
    const rate = attr(current, 'sample-rate');
    if (rate) cfg.sampleRate = Number(rate);
    // Opt-out only: "false" keeps traffic sources off on this page even when the
    // site has them on in Settings. Absent leaves it to the site setting.
    if (attr(current, 'traffic-sources') === 'false') cfg.trafficSources = false;
    // Session lifetime. Both accept "0" to disable, which is why they are parsed
    // as "attribute present" rather than truthiness — `if (idle)` would silently
    // ignore the one value a customer is most likely to set deliberately.
    const idle = ms(attr(current, 'idle-timeout'));
    if (idle !== undefined) cfg.idleTimeoutMs = idle;
    const maxSession = ms(attr(current, 'max-session'));
    if (maxSession !== undefined) cfg.maxSessionMs = maxSession;
    // Comma/space-separated allowlist of cross-origin API hosts to propagate to.
    const traceHosts = attr(current, 'trace-hosts');
    if (traceHosts) cfg.traceHosts = traceHosts.split(/[,\s]+/).map((h) => h.trim()).filter(Boolean);
    // Identity is usually set later via LogClip.identify() (post-login), but can be
    // seeded here when known at load time.
    const userId = attr(current, 'user-id');
    if (userId) cfg.userId = userId;
    const metaAttr = attr(current, 'metadata');
    if (metaAttr) {
      try {
        cfg.metadata = JSON.parse(metaAttr) as Record<string, unknown>;
      } catch {
        console.warn('[logclip] data-logclip-metadata is not valid JSON — ignored.');
      }
    }
    LogClip.init(cfg);
  }
}

window.LogClip = LogClip;
// Legacy global aliases. Customers call these from THEIR OWN application code
// (`Obsrviq.identify(...)`), so both pre-rebrand names stay bound to the same
// client instance permanently — removing one breaks their site, not ours.
(window as unknown as { Obsrviq: typeof LogClip }).Obsrviq = LogClip;
(window as unknown as { Lumera: typeof LogClip }).Lumera = LogClip;

export { LogClip };
